import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getAuth, onAuthStateChanged } from "firebase/auth";

import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  collection,
} from "firebase/firestore";

import { ref, deleteObject } from "firebase/storage";

import {
  Trash2,
  Save,
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  X,
  FileText,
  ExternalLink,
} from "lucide-react";

import { db, storage } from "../web/src/firebaseClient";
import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

const COLLECTION_NAME = "researchProjectApplications";

export default function ResearchProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [project, setProject] = useState(null);
  const [form, setForm] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [moderationLoading, setModerationLoading] = useState(false);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);

  const [rejectMessage, setRejectMessage] = useState("");
  const [approveMessage, setApproveMessage] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user || null);

      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const token = await user.getIdTokenResult(true);

        setIsAdmin(
          token.claims.role === "admin" || token.claims.admin === true
        );
      } catch (error) {
        console.error(error);
        setIsAdmin(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const refDoc = doc(db, COLLECTION_NAME, id);
        const snap = await getDoc(refDoc);

        if (!snap.exists()) {
          setProject(null);
          setForm(null);
        } else {
          const data = {
            id: snap.id,
            ...snap.data(),
          };

          setProject(data);
          setForm(data);

          setRejectMessage(
            `Merhaba ${data.applicantName || ""},

Araştırma projesi başvurunuzu değerlendirdik.

Bu aşamada başvurunuzun okul bünyesinde yürütülmesine onay veremiyoruz.

Başvurunuz ve emeğiniz için teşekkür ederiz.

Sevgiler,
İTK Girişimcilik ve İnovasyon Ekibi`
          );

          setApproveMessage(
            `Merhaba ${data.applicantName || ""},

Araştırma projesi başvurunuz değerlendirme sürecini başarıyla tamamladı.

Başvurunuz onaylanmıştır. Sürecin sonraki adımları için kısa süre içerisinde sizinle iletişime geçeceğiz.

Tebrik ederiz.

Sevgiler,
İTK Girişimcilik ve İnovasyon Ekibi`
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-6">Yükleniyor...</div>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-6">Araştırma projesi bulunamadı.</div>
      </main>
    );
  }

  const isOwner = currentUser && project.uid === currentUser.uid;

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleUpdate = async () => {
    if (!isOwner) return;

    try {
      setSaving(true);

      await updateDoc(doc(db, COLLECTION_NAME, id), {
        applicantName: form.applicantName,
        institution: form.institution,
        title: form.title,
        email: form.email,
        phone: form.phone,
        projectTitle: form.projectTitle,
        projectSummary: form.projectSummary,
        researchPurpose: form.researchPurpose,
        participantGroup: form.participantGroup,
        gradeLevel: form.gradeLevel,
        participantCount: form.participantCount,
        dataCollectionMethod: form.dataCollectionMethod,
        startDate: form.startDate,
        endDate: form.endDate,
        plannedDates: {
          startDate: form.startDate,
          endDate: form.endDate,
        },
        audioVideoRecording: form.audioVideoRecording,
        digitalToolUsage: form.digitalToolUsage,
        digitalToolExplanation: form.digitalToolExplanation,
        schoolSupport: form.schoolSupport,
        updatedAt: serverTimestamp(),
      });

      alert("Araştırma projesi güncellendi.");
    } catch (error) {
      console.error(error);
      alert("Güncelleme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isOwner) return;

    const ok = window.confirm("Bu araştırma projesini silmek istediğine emin misin?");
    if (!ok) return;

    try {
      if (Array.isArray(project.uploadedFiles)) {
        await Promise.all(
          project.uploadedFiles.map(async (file) => {
            if (!file.path) return;

            try {
              await deleteObject(ref(storage, file.path));
            } catch (error) {
              console.warn("Dosya silinemedi:", error);
            }
          })
        );
      }

      await deleteDoc(doc(db, COLLECTION_NAME, id));

      alert("Araştırma projesi silindi.");
      navigate(-1);
    } catch (error) {
      console.error(error);
      alert(error.message || "Silme sırasında hata oluştu.");
    }
  };

  const handleApprove = async () => {
    if (!isAdmin || !currentUser) return;

    if (!project.email) {
      alert("Başvuru sahibinin email adresi bulunamadı.");
      return;
    }

    if (!approveMessage.trim()) {
      alert("Lütfen onay mesajını girin.");
      return;
    }

    try {
      setModerationLoading(true);

      await updateDoc(doc(db, COLLECTION_NAME, id), {
        status: "approved",
        isApproved: true,
        approvalMessage: approveMessage.trim(),
        moderatedBy: currentUser.uid,
        moderatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "mail"), {
        to: [project.email],
        message: {
          subject: "Araştırma Projesi Başvurunuz Onaylandı",
          text: approveMessage.trim(),
        },
      });

      alert("Başvuru onaylandı ve mail gönderim kuyruğuna eklendi.");

      setApproveModalOpen(false);
      navigate("/admin/moderation");
    } catch (error) {
      console.error(error);
      alert(error.message || "Başvuru onaylanırken hata oluştu.");
    } finally {
      setModerationLoading(false);
    }
  };

  const handleReject = async () => {
    if (!isAdmin || !currentUser) return;

    if (!project.email) {
      alert("Başvuru sahibinin email adresi bulunamadı.");
      return;
    }

    if (!rejectMessage.trim()) {
      alert("Lütfen ret mesajını girin.");
      return;
    }

    try {
      setModerationLoading(true);

      await updateDoc(doc(db, COLLECTION_NAME, id), {
        status: "rejected",
        isApproved: false,
        rejectionMessage: rejectMessage.trim(),
        moderatedBy: currentUser.uid,
        moderatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "mail"), {
        to: [project.email],
        message: {
          subject: "Araştırma Projesi Başvurunuz Hakkında",
          text: rejectMessage.trim(),
        },
      });

      alert("Başvuru reddedildi ve mail gönderim kuyruğuna eklendi.");

      setRejectModalOpen(false);
      navigate("/admin/moderation");
    } catch (error) {
      console.error(error);
      alert(error.message || "Başvuru reddedilirken hata oluştu.");
    } finally {
      setModerationLoading(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-[#eef2f7]"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      <InnovationHubHeader />

      <section className="px-4 py-6">
        <div className="mx-auto max-w-[1100px]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-4 flex items-center gap-2 text-sm text-[#173b7a]"
          >
            <ArrowLeft size={16} />
            Geri
          </button>

          <div className="rounded-2xl bg-white p-6 shadow">
            <div className="mb-6">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#1565C0]/10 px-3 py-1 text-xs font-semibold text-[#1565C0]">
                  Araştırma Projesi
                </span>

                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    project.status === "approved"
                      ? "bg-green-100 text-green-700"
                      : project.status === "rejected"
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {project.status || "pending"}
                </span>
              </div>

              <h1 className="text-2xl font-semibold text-[#173b7a]">
                {project.projectTitle || "Başlıksız Araştırma Projesi"}
              </h1>

              <p className="mt-2 text-sm text-slate-500">
                Başvuru detaylarını buradan inceleyebilirsiniz.
              </p>
            </div>

            <Section title="Başvuru Sahibi Bilgileri">
              <div className="grid gap-4 md:grid-cols-2">
                <Input label="Ad Soyad" name="applicantName" value={form.applicantName || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Kurum" name="institution" value={form.institution || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Görev / Ünvan" name="title" value={form.title || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="E-posta" name="email" value={form.email || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Telefon" name="phone" value={form.phone || ""} onChange={handleChange} disabled={!isOwner} />
              </div>
            </Section>

            <Section title="Proje Bilgileri">
              <Input label="Proje Başlığı" name="projectTitle" value={form.projectTitle || ""} onChange={handleChange} disabled={!isOwner} />

              <Textarea label="Projenin Kısa Özeti" name="projectSummary" value={form.projectSummary || ""} onChange={handleChange} disabled={!isOwner} />

              <Textarea label="Araştırmanın Amacı" name="researchPurpose" value={form.researchPurpose || ""} onChange={handleChange} disabled={!isOwner} />

              <Textarea label="Okuldan Beklenen Destek" name="schoolSupport" value={form.schoolSupport || ""} onChange={handleChange} disabled={!isOwner} />
            </Section>

            <Section title="Araştırma Detayları">
              <div className="grid gap-4 md:grid-cols-2">
                <Input label="Katılımcı Grubu" name="participantGroup" value={form.participantGroup || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Sınıf / Yaş Düzeyi" name="gradeLevel" value={form.gradeLevel || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Katılımcı Sayısı" name="participantCount" value={form.participantCount || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Veri Toplama Yöntemi" name="dataCollectionMethod" value={form.dataCollectionMethod || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Başlangıç Tarihi" name="startDate" type="date" value={form.startDate || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Bitiş Tarihi" name="endDate" type="date" value={form.endDate || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Ses / Video Kaydı" name="audioVideoRecording" value={form.audioVideoRecording || ""} onChange={handleChange} disabled={!isOwner} />
                <Input label="Dijital Araç Kullanımı" name="digitalToolUsage" value={form.digitalToolUsage || ""} onChange={handleChange} disabled={!isOwner} />
              </div>

              <Textarea label="Dijital Araç Açıklaması" name="digitalToolExplanation" value={form.digitalToolExplanation || ""} onChange={handleChange} disabled={!isOwner} />
            </Section>

            <Section title="Yüklenen Dosyalar">
              {!Array.isArray(project.uploadedFiles) ||
              project.uploadedFiles.length === 0 ? (
                <div className="rounded-xl bg-[#f8fbff] p-4 text-sm text-slate-500">
                  Yüklenmiş dosya bulunmuyor.
                </div>
              ) : (
                <div className="space-y-3">
                  {project.uploadedFiles.map((file, index) => (
                    <button
                      key={`${file.path || file.url}-${index}`}
                      type="button"
                      onClick={() => {
                        if (file.url) window.location.href = file.url;
                      }}
                      className="flex w-full items-center justify-between gap-4 rounded-xl border border-gray-100 bg-[#f8fbff] p-4 text-left transition hover:bg-blue-50"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1565C0]/10 text-[#1565C0]">
                          <FileText size={18} />
                        </div>

                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[#173b7a]">
                            {file.name || "Dosya"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {file.type || "Dosya"}{" "}
                            {file.size ? `• ${(file.size / 1024 / 1024).toFixed(2)} MB` : ""}
                          </p>
                        </div>
                      </div>

                      <ExternalLink size={17} className="text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </Section>

            {(isOwner || isAdmin) && (
              <div className="mt-6 flex flex-wrap justify-end gap-2">
                {isOwner && (
                  <>
                    <button
                      type="button"
                      onClick={handleUpdate}
                      disabled={saving}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                      Güncelle
                    </button>

                    <button
                      type="button"
                      onClick={handleDelete}
                      className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                    >
                      <Trash2 size={14} />
                      Sil
                    </button>
                  </>
                )}

                {isAdmin && (
                  <>
                    <button
                      type="button"
                      onClick={() => setRejectModalOpen(true)}
                      disabled={moderationLoading}
                      className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60"
                    >
                      <XCircle size={14} />
                      Reddet
                    </button>

                    <button
                      type="button"
                      onClick={() => setApproveModalOpen(true)}
                      disabled={moderationLoading}
                      className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-60"
                    >
                      <CheckCircle size={14} />
                      Onayla
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {rejectModalOpen && (
        <MailModal
          title="Ret Maili"
          value={rejectMessage}
          onChange={setRejectMessage}
          onClose={() => setRejectModalOpen(false)}
          onSend={handleReject}
          loading={moderationLoading}
          buttonClassName="bg-red-600 hover:bg-red-700"
        />
      )}

      {approveModalOpen && (
        <MailModal
          title="Onay Maili"
          value={approveMessage}
          onChange={setApproveMessage}
          onClose={() => setApproveModalOpen(false)}
          onSend={handleApprove}
          loading={moderationLoading}
          buttonClassName="bg-green-600 hover:bg-green-700"
        />
      )}

      <InnovationHubFooter />
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-5 rounded-2xl border border-gray-100 bg-white p-4">
      <h2 className="mb-4 text-base font-semibold text-[#173b7a]">
        {title}
      </h2>

      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Input({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-sm text-[#173b7a]">{label}</span>
      <input {...props} className="mt-1 w-full rounded-xl border p-3" />
    </label>
  );
}

function Textarea({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-sm text-[#173b7a]">{label}</span>
      <textarea {...props} rows={4} className="mt-1 w-full rounded-xl border p-3" />
    </label>
  );
}

function MailModal({
  title,
  value,
  onChange,
  onClose,
  onSend,
  loading,
  buttonClassName,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold text-[#173b7a]">
              {title}
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              Mail içeriğini düzenleyebilirsiniz.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-100 p-2 text-gray-500 hover:bg-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={12}
          className="w-full resize-none rounded-xl border border-gray-200 p-4 text-sm leading-relaxed outline-none focus:border-[#1565C0]"
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200"
          >
            Vazgeç
          </button>

          <button
            type="button"
            onClick={onSend}
            disabled={loading}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${buttonClassName}`}
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            Gönder
          </button>
        </div>
      </div>
    </div>
  );
}